import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useProfileCompletion } from '../hooks/useProfileCompletion'
import { ProfileCompletionModal } from './ProfileCompletionModal'
import { ProfileIncompleteModal } from './ProfileIncompleteModal'

interface ProfileGuardedRouteProps {
  children: React.ReactNode
}

export function ProfileGuardedRoute({ children }: ProfileGuardedRouteProps): React.ReactNode {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  const { isCheckingCompletion, isProfileComplete, requiresProfileCompletion, hasCheckedCompletion } = useProfileCompletion()
  const location = useLocation()

  // Simple modal state for profile page
  const showProfileModal = requiresProfileCompletion && !isProfileComplete && location.pathname === '/profile'

  // Simplified loading logic - handle auth loading first
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // Wait for user data to be available
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // CRITICAL FIX: Wait for profile completion check to finish before making decisions
  // This prevents the flashing modal issue where we show "incomplete" then "complete"
  if (requiresProfileCompletion && (isCheckingCompletion || !hasCheckedCompletion)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking profile...</p>
        </div>
      </div>
    )
  }

  // Handle profile completion for users with role='user' and type='smb'
  if (requiresProfileCompletion && !isProfileComplete) {
    // Only allow access to Support, Profile, and Profile edit routes
    const allowedRoutes = ['/support', '/profile', '/profile/edit', '/profile/setup']
    const isAllowedRoute = allowedRoutes.some(route => location.pathname.startsWith(route))
    
    if (isAllowedRoute) {
      return (
        <>
          {children}
          {showProfileModal && (
            <ProfileCompletionModal 
              isOpen={true} 
              onClose={() => {}} // No close option
            />
          )}
        </>
      )
    }

    // For non-allowed routes, show modal and redirect on confirm
    const handleModalConfirm = () => {
      window.location.href = '/profile'
    }

    return (
      <>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <ProfileIncompleteModal 
          isOpen={true} 
          onConfirm={handleModalConfirm}
        />
      </>
    )
  }

  // Normal authenticated route access
  return <>{children}</>
} 